import { useState, useMemo, useCallback } from "react";
import { DEFAULTS } from "../constants/defaults";

const useSIPCalculation = () => {
  const [sipTarget, setSipTarget] = useState(DEFAULTS.sipTarget);
  const [sipPeriod, setSipPeriod] = useState(DEFAULTS.sipPeriod);
  const [sipReturnRate, setSipReturnRate] = useState(DEFAULTS.sipReturnRate);
  const [sipAmount, setSipAmount] = useState(DEFAULTS.sipAmount);
  const [sipCalculationType, setSipCalculationType] = useState("interest");

  const calculateSIP = useCallback((targetAmount, annualRate, months) => {
    if (months <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;
    if (monthlyRate === 0) return targetAmount / months;
    return (targetAmount * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
  }, []);

  // Memoized SIP calculation — O(n) schedule instead of original O(n²)
  const sipCalculation = useMemo(() => {
    const requiredSip =
      sipAmount > 0
        ? sipAmount
        : sipTarget > 0
        ? calculateSIP(sipTarget, sipReturnRate, sipPeriod)
        : 0;

    const totalSipInvestment = requiredSip * sipPeriod;
    const monthlyReturnRate = sipReturnRate / 12 / 100;

    // Build schedule in O(n) using the recurrence: value[m] = value[m-1]*(1+r) + SIP
    const sipSchedule = [];
    let value = 0;
    for (let month = 1; month <= sipPeriod; month++) {
      value = value * (1 + monthlyReturnRate) + requiredSip;
      const cumulativeInvestment = requiredSip * month;
      sipSchedule.push({
        month,
        sipAmount: requiredSip,
        cumulativeInvestment,
        cumulativeReturns: value,
        growth: value - cumulativeInvestment,
      });
    }

    // Total returns = final value grown one more period (SIP invested at start of month)
    const sipReturns = value * (1 + monthlyReturnRate);
    const netBenefit = sipReturns - totalSipInvestment;

    return {
      targetAmount: sipTarget,
      calculationType: sipCalculationType,
      requiredSip,
      totalSipInvestment,
      sipReturns,
      netBenefit,
      sipSchedule,
    };
  }, [sipTarget, sipPeriod, sipReturnRate, sipAmount, sipCalculationType, calculateSIP]);

  return {
    sipTarget, setSipTarget,
    sipPeriod, setSipPeriod,
    sipReturnRate, setSipReturnRate,
    sipAmount, setSipAmount,
    sipCalculationType, setSipCalculationType,
    sipCalculation,
    calculateSIP,
  };
};

export default useSIPCalculation;
